/**
 * Memory Calculator — 固定商业启发式（非 LLM）
 *
 * 只计算 fatigue / ratios / weekly balance，不做素材决策。
 */
import type { WeeklyActivityCounts, WeeklyBalance } from './memoryTypes';

function clamp01(n: number): number {
  if (Number.isNaN(n) || !Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

/** 本周商业语气占比 */
export function calculateCommercialRatio(counts: WeeklyActivityCounts): number {
  if (counts.activeDays <= 0) return 0;
  return clamp01(counts.commercialDays / counts.activeDays);
}

/** 本周开发/互动向占比 */
export function calculateInteractionRatio(counts: WeeklyActivityCounts): number {
  if (counts.activeDays <= 0) return 0;
  return clamp01(counts.outreachDays / counts.activeDays);
}

/** 本周生活/信任向占比 */
export function calculateTrustRatio(counts: WeeklyActivityCounts): number {
  if (counts.activeDays <= 0) return 0;
  return clamp01(counts.lifeDays / counts.activeDays);
}

/**
 * 疲劳分：口播密集、连续商业、缺休息、影音偏高 → 升高
 */
export function calculateFatigue(counts: WeeklyActivityCounts): number {
  let score = 0;
  score += Math.min(0.35, counts.talkingDays * 0.15);
  score += Math.min(0.25, counts.videoDays * 0.1);
  score += Math.min(0.3, counts.consecutiveCommercialDays * 0.15);
  if (counts.yesterdayHadTalking) score += 0.2;
  if (counts.restDays === 0 && counts.activeDays >= 4) score += 0.15;
  if (counts.weekSelfieCount >= 2) score += 0.1;
  // 有休息则下调
  score -= Math.min(0.25, counts.restDays * 0.12);
  return clamp01(score);
}

/** 汇总周平衡指标 */
export function calculateWeeklyBalance(counts: WeeklyActivityCounts): WeeklyBalance {
  const commercial_ratio = calculateCommercialRatio(counts);
  const trust_ratio = calculateTrustRatio(counts);
  const interaction_ratio = calculateInteractionRatio(counts);
  const fatigue_score = calculateFatigue(counts);
  const energy_level = clamp01(1 - fatigue_score + Math.min(0.15, counts.restDays * 0.05));
  return {
    commercial_ratio,
    trust_ratio,
    interaction_ratio,
    energy_level,
    fatigue_score,
  };
}
